import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Menu as MenuIcon,
  StopCircle as EndSessionIcon,
  Quiz as QuizIcon,
  AddCircleOutline as AddTopicIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTutorSession } from '../hooks/useTutorSession';
import TopicListPanel, { TopicListItem } from '../components/session/TopicListPanel';
import FocusModeWrapper from '../components/session/FocusModeWrapper';
import QuizPanel from '../components/session/QuizPanel';
import LiveQuizDispatch from '../components/session/LiveQuizDispatch';
import RichTextCanvas, { RichTextCanvasRef } from '../components/canvas/RichTextCanvas';
import PublishButton from '../components/gn/PublishButton';
import AddTopicDialog from '../components/session/AddTopicDialog';
import ChatPanel from '../components/aicanvas/ChatPanel';
import CueChip from '../components/gn/CueChip';
import AnswerCheckPanel from '../components/gn/AnswerCheckPanel';
import NarrationControl, { NarrationControlRef } from '../components/aicanvas/NarrationControl';
import { useCanvasActionStreaming } from '../hooks/useCanvasActionStreaming';
import { useCueObserver } from '../hooks/useCueObserver';
import type { CueType } from '../services/gnCueService';
import type { CanvasAction } from '../services/aiCanvasActionService';
import { generateAiDraft } from '../services/tutorTopicContentService';
import { getStructure, TutorStructureNode } from '../services/tutorStructureService';
import { listSchedules } from '../services/tutorScheduleService';
import { listNodeQuizzes, getNodeQuiz, TutorQuizDTO } from '../services/tutorQuizService';
import { LiveQuizSessionDTO, getTaughtRecords, toggleTaughtStatus } from '../services/tutorSessionService';
import { generateJSON } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';

const TutorSessionWorkspacePage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const id = Number(courseId);

  const { session, loading, error, loadActiveSession, startSession, endSession, updateCurrentPage } =
    useTutorSession(id);

  const [topics, setTopics] = useState<TopicListItem[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [focusMode, setFocusMode] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [startError, setStartError] = useState<string | null>(null);
  const [structureNodes, setStructureNodes] = useState<TutorStructureNode[]>([]);
  const [addTopicDialogOpen, setAddTopicDialogOpen] = useState(false);
  const [quizPanelOpen, setQuizPanelOpen] = useState(false);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [topicQuiz, setTopicQuiz] = useState<TutorQuizDTO | null>(null);
  const [activeQuizSession, setActiveQuizSession] = useState<LiveQuizSessionDTO | null>(null);
  const [taughtNodeIds, setTaughtNodeIds] = useState<Set<number>>(new Set());
  const canvasRef = useRef<RichTextCanvasRef>(null);
  const [publishRefreshKey, setPublishRefreshKey] = useState(0);
  const [aiGenerating, setAiGenerating] = useState(false);
  const narrationRef = useRef<NarrationControlRef>(null);
  const wasGeneratingRef = useRef(false);

  const appendToCanvas = useCallback((blockType: 'TEXT' | 'IMAGE' | 'CHART', attrs: Record<string, any>) => {
    canvasRef.current?.appendBlock(blockType, attrs);
  }, []);

  const { triggerAction, stop: stopAction, generating: actionGenerating, streamingText, lastVerdict, clearVerdict } =
    useCanvasActionStreaming('tutor', id, selectedNodeId ?? 0, appendToCanvas, narrationRef);
  const { offer: cueOffer, observeCandidate, dismiss: dismissCue, clear: clearCue } =
    useCueObserver('tutor', id, selectedNodeId ?? null);
  const lastObservedTextRef = useRef('');

  useEffect(() => {
    if (wasGeneratingRef.current && !actionGenerating) {
      clearCue();
    }
    wasGeneratingRef.current = actionGenerating;
  }, [actionGenerating, clearCue]);

  const CUE_TO_ACTION: Record<CueType, CanvasAction> = {
    SOLVE: 'SOLVE_PROBLEM',
    UNDERSTAND_PROBLEM: 'UNDERSTAND_PROBLEM',
    EXPLAIN_TOPIC: 'UNDERSTAND_TOPIC',
    CHECK_ANSWER: 'CHECK_ANSWER',
    IMAGE_QUESTION: 'IMAGE_QUESTION',
  };

  const handleAcceptCue = (type: CueType) => {
    const text = lastObservedTextRef.current;
    if (type === 'CHECK_ANSWER') {
      triggerAction('CHECK_ANSWER', '', { questionText: text, attemptText: text });
    } else {
      triggerAction(CUE_TO_ACTION[type], text);
    }
  };

  const loadTopics = useCallback(async () => {
    setTopicsLoading(true);
    try {
      const [structure, schedules] = await Promise.all([getStructure(id), listSchedules(id)]);
      setStructureNodes(structure.nodes);
      const scheduleMap: Record<number, string> = {};
      schedules.forEach((s) => { scheduleMap[s.nodeId] = s.targetDate; });
      const flatTopics: TopicListItem[] = [];
      const flatten = (nodes: typeof structure.nodes) => {
        nodes.forEach((n) => {
          if (n.nodeType !== 'WRAPPER') {
            flatTopics.push({
              nodeId: n.id,
              label: n.label,
              scheduledDate: scheduleMap[n.id] ?? null,
              isTaught: false,
            });
          }
          if (n.children?.length) flatten(n.children);
        });
      };
      flatten(structure.nodes);
      setTopics(flatTopics);
    } catch {
      setStructureNodes([]);
      setTopics([]);
    } finally {
      setTopicsLoading(false);
    }
  }, [id]);

  // Load structure + schedules to populate topic list
  useEffect(() => {
    loadTopics();
  }, [loadTopics]);

  const handleTopicCreated = () => {
    loadTopics();
  };

  // On mount: load existing active session or start one
  useEffect(() => {
    loadActiveSession().then(() => {
      // If loadActiveSession found nothing, start a new session
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartIfNeeded = async () => {
    if (session) return;
    try {
      await startSession();
    } catch (err: any) {
      if (err?.response?.status !== 409) {
        setStartError(err?.response?.data?.message || err.message || 'Failed to start session');
      } else {
        // 409 = already active — reload
        await loadActiveSession();
      }
    }
  };

  useEffect(() => {
    if (!loading && session === null) {
      handleStartIfNeeded();
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getTaughtRecords(id)
      .then(records => {
        setTaughtNodeIds(new Set(records.filter(r => r.isTaught).map(r => r.nodeId)));
      })
      .catch(() => {});
  }, [id]);

  const handleToggleTaught = async (nodeId: number) => {
    try {
      const updated = await toggleTaughtStatus(id, nodeId);
      setTaughtNodeIds(prev => {
        const next = new Set(prev);
        updated.isTaught ? next.add(nodeId) : next.delete(nodeId);
        return next;
      });
    } catch {
      // silently ignore
    }
  };

  useEffect(() => {
    if (!selectedNodeId) { setTopicQuiz(null); return; }
    listNodeQuizzes(id, selectedNodeId)
      .then(summaries => {
        const active = summaries.find(s => s.status === 'ACTIVE') ?? summaries[0] ?? null;
        if (active) return getNodeQuiz(id, selectedNodeId, active.id);
        return null;
      })
      .then(quiz => setTopicQuiz(quiz))
      .catch(() => setTopicQuiz(null));
  }, [selectedNodeId, id]);

  const handleTopicSelect = async (nodeId: number) => {
    setSelectedNodeId(nodeId);
    if (session?.status === 'ACTIVE') {
      await updateCurrentPage(nodeId, 0).catch(() => {});
    }
  };

  const handleAiGenerate = async () => {
    if (!selectedNodeId) return;
    setAiGenerating(true);
    try {
      const { draftContent } = await generateAiDraft(id, selectedNodeId);
      const json = generateJSON(draftContent, [StarterKit]);
      canvasRef.current?.loadContent(json);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleEndSession = async () => {
    if (!window.confirm('End this session? Students will see "Session has ended" in their Playground.')) return;
    try {
      await endSession();
      navigate(`/tutor/courses/${id}`);
    } catch {
      // error is already set in the hook
    }
  };

  if (loading || topicsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (topics.length === 0) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar variant="dense">
            <Tooltip title="Back to course">
              <IconButton size="small" onClick={() => navigate(`/tutor/courses/${id}`)}>
                <BackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, ml: 1 }}>
              Session Workspace
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 420 }}>
            <AddTopicIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>No topics yet</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Add your first topic to start the session.
            </Typography>
            <Button
              variant="contained"
              onClick={() => setAddTopicDialogOpen(true)}
              startIcon={<AddTopicIcon />}
            >
              Add Topic
            </Button>
          </Box>
        </Box>
        <AddTopicDialog
          open={addTopicDialogOpen}
          onClose={() => setAddTopicDialogOpen(false)}
          courseId={id}
          structureNodes={structureNodes}
          initialParentId={selectedNodeId}
          onCreated={handleTopicCreated}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar — hidden in focus mode */}
      {!focusMode && (
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar variant="dense" sx={{ gap: 1 }}>
            <Tooltip title="Back to course">
              <IconButton size="small" onClick={() => navigate(`/tutor/courses/${id}`)}>
                <BackIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setPanelOpen((v) => !v)}>
              <MenuIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
              Session Workspace
              {session?.status === 'ACTIVE' && (
                <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                  ● LIVE
                </Typography>
              )}
            </Typography>
            {(error || startError) && (
              <Alert severity="error" sx={{ py: 0, fontSize: '0.75rem' }}>
                {error || startError}
              </Alert>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddTopicIcon />}
              onClick={() => setAddTopicDialogOpen(true)}
            >
              Add Topic
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleAiGenerate}
              disabled={!selectedNodeId || aiGenerating}
            >
              {aiGenerating ? 'Generating…' : 'Generate with AI'}
            </Button>
            <Button
              size="small"
              variant={quizPanelOpen ? 'contained' : 'outlined'}
              startIcon={<QuizIcon />}
              onClick={() => setQuizPanelOpen(v => !v)}
              disabled={!selectedNodeId}
            >
              Quiz
            </Button>
            <Button
              size="small"
              variant={chatPanelOpen ? 'contained' : 'outlined'}
              startIcon={<ChatIcon />}
              onClick={() => setChatPanelOpen(v => !v)}
              disabled={!selectedNodeId}
            >
              Ask AI
            </Button>
            <NarrationControl ref={narrationRef} />
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<EndSessionIcon />}
              onClick={handleEndSession}
              disabled={session?.status !== 'ACTIVE'}
            >
              End Session
            </Button>
          </Toolbar>
        </AppBar>
      )}

      {/* Main content area */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <FocusModeWrapper
              focusMode={focusMode}
              onToggleFocusMode={() => setFocusMode((v) => !v)}
              sidebar={
                <TopicListPanel
                  open={panelOpen}
                  topics={topics.map(t => ({ ...t, isTaught: taughtNodeIds.has(t.nodeId) }))}
                  selectedNodeId={selectedNodeId}
                  onTopicSelect={handleTopicSelect}
                  onToggleTaught={handleToggleTaught}
                />
              }
            >
              {selectedNodeId ? (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Publish model (spec 046 FR-033/FR-036): saves are tutor-private; students
                      only see the last explicitly published version. */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1 }}>
                    <PublishButton courseId={id} nodeId={selectedNodeId} refreshKey={publishRefreshKey} />
                  </Box>
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <RichTextCanvas
                      key={selectedNodeId}
                      ref={canvasRef}
                      courseId={id}
                      nodeId={selectedNodeId}
                      onPasteDetected={(text, position) => {
                        lastObservedTextRef.current = text;
                        observeCandidate(text, position);
                      }}
                      onSaveStateChange={(status) => {
                        if (status === 'saved') setPublishRefreshKey(k => k + 1);
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%" p={4}>
                  <Typography color="text.secondary">Select a topic from the panel to begin.</Typography>
                </Box>
              )}
            </FocusModeWrapper>
          </Box>

          {/* AI chat — side thread, not mixed into the canvas document (FR-015) */}
          <Collapse in={chatPanelOpen && !!selectedNodeId} orientation="horizontal">
            <Paper
              elevation={0}
              sx={{ width: 320, height: '100%', borderLeft: '1px solid', borderColor: 'divider' }}
            >
              {selectedNodeId && <ChatPanel role="tutor" courseId={id} nodeId={selectedNodeId} />}
            </Paper>
          </Collapse>
        </Box>

        {/* Quiz panel — shown when quiz button toggled, hidden in focus mode */}
        {quizPanelOpen && !focusMode && (
          <Paper elevation={2} sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2, maxHeight: 340, overflowY: 'auto' }}>
            <QuizPanel
              courseId={id}
              topicNodeId={selectedNodeId}
              quiz={topicQuiz}
              activeQuizSession={activeQuizSession}
              onQuizDispatched={session => setActiveQuizSession(session)}
              onQuizClosed={() => setActiveQuizSession(null)}
            />
            {activeQuizSession && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <LiveQuizDispatch
                  courseId={id}
                  quizSessionId={activeQuizSession.id}
                  isActive={activeQuizSession.status === 'ACTIVE'}
                  questionCount={activeQuizSession.questionIds.length}
                />
              </>
            )}
          </Paper>
        )}
      </Box>

      <AddTopicDialog
        open={addTopicDialogOpen}
        onClose={() => setAddTopicDialogOpen(false)}
        courseId={id}
        structureNodes={structureNodes}
        initialParentId={selectedNodeId}
        onCreated={handleTopicCreated}
      />

      <CueChip
        offer={cueOffer}
        generating={actionGenerating}
        streamingText={streamingText}
        onAccept={handleAcceptCue}
        onStop={stopAction}
        onDismiss={dismissCue}
      />
      {lastVerdict && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, width: 360, zIndex: 1300 }}>
          <AnswerCheckPanel
            verdict={lastVerdict}
            onRevealSolution={() => triggerAction('REVEAL_SOLUTION', '', { questionText: lastObservedTextRef.current })}
            onDismiss={clearVerdict}
          />
        </Box>
      )}
    </Box>
  );
};

export default TutorSessionWorkspacePage;
